import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpcClient, useTRPC } from "@/trpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const formSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
});

type FormValues = z.infer<typeof formSchema>;

export default function OrgAccountDetailsOnboardingForm({
  onComplete,
}: {
  onComplete: () => void;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const updateUserMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string }) =>
      trpcClient.user.updateOrgUser.mutate(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.user.getUser.queryKey(),
      });
      onComplete();
    },
    onError: () => {
      toast.error("Failed to save your details. Please try again.");
    },
  });

  function onSubmit(values: FormValues) {
    updateUserMutation.mutate({
      firstName: values.firstName,
      lastName: values.lastName,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your first name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your last name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={updateUserMutation.isPending}
          className="w-full"
        >
          {updateUserMutation.isPending ? "Saving..." : "Continue"}
        </Button>
      </form>
    </Form>
  );
}
